import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useCallback } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { FormLabelText } from '../../../components/Form/FormComponents';
import { MprItemsPanel } from './MprItemsPanel';
import { MprPeriodsPanel } from './MprPeriodsPanel';
import { MprLayout } from './MultiPeriodReporting.schemas';
import { useMprColumnConfigs } from './useMprColumnConfigs';

interface IMultiPeriodReportingSettingsProps {
  onClose: () => void;
}

export function MultiPeriodReportingSettings({ onClose }: IMultiPeriodReportingSettingsProps) {
  const { resetDrafts } = useMprColumnConfigs();
  const onCancel = useCallback(() => {
    resetDrafts();
    onClose();
  }, [onClose, resetDrafts]);

  return (
    <BasicDialog open onClose={onCancel} title={'Report Settings'} fullWidth maxWidth={'lg'}>
      <Box padding={'0 2.5rem 1.5rem'}>
        <Box mb={'1.5rem'}>
          <LayoutToggle />
        </Box>
        <Box display={'grid'} gridTemplateColumns={'minmax(0, 1fr) minmax(0, 1fr)'} gap={'2rem'}>
          <MprItemsPanel />
          <MprPeriodsPanel />
        </Box>
        <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} mt={'1.5rem'} gap={'1rem'}>
          <Button variant={'outlined'} color={'secondary'} onClick={onCancel} size={'medium'}>
            Cancel
          </Button>
          <SaveAndRefreshButton onClose={onClose} />
        </Stack>
      </Box>
    </BasicDialog>
  );
}

function SaveAndRefreshButton({ onClose }: { onClose: () => void }) {
  const { saveConfigState } = useMprColumnConfigs();

  const onClick = useCallback(() => {
    saveConfigState();
    // report is refetched on column config change (see useMprReportData)
    onClose();
  }, [onClose, saveConfigState]);

  return (
    <Button variant={'contained'} color={'secondary'} onClick={onClick}>
      Save and Refresh
    </Button>
  );
}

function LayoutToggle() {
  const { draft, setDraft, draftColumnConfigs } = useMprColumnConfigs();
  const { items, periods, layout } = draft;
  const counts = `${items.length} item${items.length === 1 ? '' : 's'} × ${periods.length} period${periods.length === 1 ? '' : 's'}`;
  const summary =
    layout === MprLayout.rows
      ? `${counts}, one row per company and item`
      : `${counts} = ${draftColumnConfigs.length} column${draftColumnConfigs.length === 1 ? '' : 's'}`;

  return (
    <Stack gap={'0.25rem'}>
      <FormLabelText label='Layout' />
      <ToggleButtonGroup
        aria-label='Layout'
        color='secondary'
        size='small'
        exclusive
        value={layout}
        onChange={(_, value: MprLayout | null) => {
          if (value != null) {
            setDraft((prev) => ({ ...prev, layout: value }));
          }
        }}
      >
        <ToggleButton value={MprLayout.period}>Columns by period</ToggleButton>
        <ToggleButton value={MprLayout.item}>Columns by item</ToggleButton>
        <ToggleButton value={MprLayout.rows}>Row per item</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant='caption' color='text.secondary'>
        {summary}
      </Typography>
    </Stack>
  );
}
